
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/base/Button';

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showModal, setShowModal] = useState(false);

  const heroSlides = [
    {
      title: 'Rasa Autentik Asli Jepang',
      subtitle: 'Satu-Satunya di Bukittinggi',
      description:
        'Nikmati pengalaman kuliner Jepang yang tak terlupakan dengan cita rasa autentik dan penyajian modern',
      image:
        'https://public.readdy.ai/ai/img_res/27a6044f36107bb608c92b165fa4b6e6.jpg',
      cta: 'Pesan Sekarang',
    },
    {
      title: 'Menu Premium',
      subtitle: '最高品質の料理',
      description:
        'Koleksi menu premium dengan bahan-bahan segar langsung dari Jepang, dimasak oleh chef berpengalaman',
      image:
        'https://readdy.ai/api/search-image?query=Premium%20Japanese%20cuisine%20display%2C%20sushi%2C%20ramen%2C%20tempura%2C%20sashimi%2C%20elegant%20food%20presentation%2C%20traditional%20Japanese%20ceramic%20dishes%2C%20high-end%20restaurant%20quality%2C%20appetizing%20food%20photography&width=1200&height=600&seq=hero-slide-002&orientation=landscape',
      cta: 'Lihat Menu',
    },
    {
      title: 'Suasana Futuristik',
      subtitle: '未来的な雰囲気',
      description:
        'Rasakan pengalaman makan di restoran Jepang dengan konsep futuristik yang memukau dan teknologi modern',
      image:
        'https://readdy.ai/api/search-image?query=Futuristic%20Japanese%20restaurant%20ambiance%2C%20neon%20lighting%2C%20cyberpunk%20aesthetic%2C%20traditional%20meets%20modern%2C%20Torii%20gate%20with%20neon%20lights%2C%20Mount%20Fuji%20backdrop%2C%20technological%20elements%2C%20atmospheric%20dining&width=1200&height=600&seq=hero-slide-003&orientation=landscape',
      cta: 'Reservasi',
    },
  ];

  // Auto‑slide carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroSlides.length]);

  // Smooth scroll helper with basic safety check
  const scrollToSection = (sectionId: string) => {
    try {
      const element = document.querySelector(sectionId);
      if (element instanceof HTMLElement) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (err) {
      console.error(`Unable to scroll to ${sectionId}:`, err);
    }
  };

  // Handle close modal
  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Handle show welcome modal
  const handleShowWelcomeModal = () => {
    setShowModal(true);
  };

  return (
    <section className="relative h-screen overflow-hidden bg-black">
      {/* Background Slider */}
      <div className="absolute inset-0">
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent"></div>
          </div>
        ))}
      </div>

      {/* Floating Food Icons Animation */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="animate-float-slow absolute top-20 right-20 text-6xl">🍜</div>
        <div className="animate-float-medium absolute top-40 right-1/3 text-5xl">🍣</div>
        <div className="animate-float-fast absolute top-60 right-1/4 text-4xl">🐙</div>
        <div className="animate-float-slow absolute bottom-40 right-20 text-5xl">🍵</div>
      </div>

      {/* Neon Grid Lines */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(239, 68, 68, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(239, 68, 68, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        ></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-6 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-left">
              {/* Japanese Characters */}
              <div className="mb-6">
                <span
                  className="text-6xl md:text-8xl font-black text-transparent bg-gradient-to-r from-red-500 to-yellow-400 bg-clip-text block leading-tight"
                  style={{ fontFamily: '"Noto Sans JP", serif' }}
                >
                  竹や
                </span>
                <span
                  className="text-3xl md:text-4xl font-bold text-white mt-2 block"
                  style={{ fontFamily: '"Orbitron", sans-serif' }}
                >
                  TAKEYA
                </span>
              </div>

              {/* Dynamic Content */}
              <div className="mb-8">
                <h1 className="text-4xl md:text-6xl font-black text-white mb-4 leading-tight">
                  {heroSlides[currentSlide].title}
                </h1>
                <h2
                  className="text-xl md:text-2xl text-red-400 font-semibold mb-4"
                  style={{ fontFamily: '"Noto Sans JP", serif' }}
                >
                  {heroSlides[currentSlide].subtitle}
                </h2>
                <p className="text-gray-300 text-lg md:text-xl leading-relaxed max-w-xl">
                  {heroSlides[currentSlide].description}
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/customer">
                  <Button
                    variant="primary"
                    size="lg"
                    className="shadow-2xl shadow-red-500/50 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-restaurant-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                    {heroSlides[currentSlide].cta}
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => scrollToSection('#menu')}
                  className="shadow-2xl shadow-yellow-500/50 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                  Lihat Menu
                </Button>
              </div>

              {/* Features */}
              <div className="grid grid-cols-3 gap-6 max-w-lg">
                <div className="text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-2">
                    <i className="ri-star-line text-white w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <p className="text-white font-semibold text-sm">Premium</p>
                  <p className="text-gray-400 text-xs">Quality</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-2">
                    <i className="ri-time-line text-white w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <p className="text-white font-semibold text-sm">Fast</p>
                  <p className="text-gray-400 text-xs">Delivery</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                    <i className="ri-heart-line text-white w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <p className="text-white font-semibold text-sm">Fresh</p>
                  <p className="text-gray-400 text-xs">Ingredients</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Welcome Modal Trigger Button */}
      <div className="absolute top-6 right-6 z-20">
        <button
          onClick={handleShowWelcomeModal}
          className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg hover:shadow-xl focus:ring-red-500 px-8 py-4 text-lg rounded-full font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
        >
          <i className="ri-information-line mr-2 w-5 h-5 flex items-center justify-center"></i>
          Info Restoran
        </button>
      </div>

      {/* Welcome Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-3xl border border-red-500/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center space-x-4">
                  <span className="text-4xl" style={{ fontFamily: '"Noto Sans JP", serif' }}>竹</span>
                  <div>
                    <h3 className="text-2xl font-bold text-white">TAKEYA RESTAURANT</h3>
                    <p className="text-red-400">Authentic Japanese Experience</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg hover:shadow-xl focus:ring-red-500 px-8 py-4 text-lg rounded-full font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  Tutup
                </button>
              </div>

              {/* Modal Content */}
              <div className="space-y-8">
                {/* Restaurant Info */}
                <div className="bg-gradient-to-r from-red-900/20 to-yellow-900/20 rounded-2xl p-6 border border-red-500/20">
                  <h4 className="text-xl font-bold text-white mb-4">
                    <i className="ri-restaurant-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                    Tentang Takeya
                  </h4>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Takeya Restaurant menghadirkan pengalaman kuliner Jepang autentik di jantung Bukittinggi. 
                    Dengan konsep futuristik yang memadukan tradisi dan modernitas, kami menyajikan hidangan 
                    premium dari bahan-bahan segar berkualitas tinggi.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <i className="ri-time-line text-red-400 w-4 h-4 flex items-center justify-center"></i>
                      <span className="text-gray-300">Buka: 10:00 - 22:00</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-phone-line text-red-400 w-4 h-4 flex items-center justify-center"></i>
                      <span className="text-gray-300">+62 812-7543-743</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-map-pin-line text-red-400 w-4 h-4 flex items-center justify-center"></i>
                      <span className="text-gray-300">Jl. Biaro, Agam</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-wifi-line text-red-400 w-4 h-4 flex items-center justify-center"></i>
                      <span className="text-gray-300">Free WiFi tersedia</span>
                    </div>
                  </div>
                </div>

                {/* Special Features */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-blue-900/20 rounded-2xl p-4 border border-blue-500/20 text-center">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-award-line text-white w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <h5 className="font-bold text-white mb-2">Premium Quality</h5>
                    <p className="text-gray-300 text-sm">Bahan segar langsung dari Jepang</p>
                  </div>
                  <div className="bg-green-900/20 rounded-2xl p-4 border border-green-500/20 text-center">
                    <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-user-star-line text-white w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <h5 className="font-bold text-white mb-2">Expert Chef</h5>
                    <p className="text-gray-300 text-sm">Chef berpengalaman dari Jepang</p>
                  </div>
                  <div className="bg-yellow-900/20 rounded-2xl p-4 border border-yellow-500/20 text-center">
                    <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-sparkling-line text-white w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <h5 className="font-bold text-white mb-2">Modern Ambiance</h5>
                    <p className="text-gray-300 text-sm">Suasana futuristik yang nyaman</p>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="space-y-4">
                  <h4 className="text-xl font-bold text-white">
                    <i className="ri-menu-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                    Menu Favorit
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gray-800/50 rounded-2xl p-4 border border-gray-700">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center text-2xl">🍜</div>
                        <div>
                          <h6 className="font-bold text-white">Tonkotsu Ramen</h6>
                          <p className="text-gray-400 text-sm">Kuah tulang premium</p>
                        </div>
                        <span className="text-yellow-400 font-bold ml-auto">Rp 75.000</span>
                      </div>
                    </div>
                    <div className="bg-gray-800/50 rounded-2xl p-4 border border-gray-700">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center text-2xl">🍣</div>
                        <div>
                          <h6 className="font-bold text-white">Omakase Sushi</h6>
                          <p className="text-gray-400 text-sm">12 pieces premium</p>
                        </div>
                        <span className="text-yellow-400 font-bold ml-auto">Rp 150.000</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Call to Action */}
                <div className="bg-gradient-to-r from-red-600/20 to-yellow-600/20 rounded-2xl p-6 border border-red-500/30 text-center">
                  <h4 className="text-xl font-bold text-white mb-4">Siap untuk Pengalaman Tak Terlupakan?</h4>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/customer">
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={handleCloseModal}
                        className="shadow-2xl shadow-red-500/50 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-restaurant-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                        Pesan Sekarang
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => {
                        handleCloseModal();
                        scrollToSection('#menu');
                      }}
                      className="shadow-2xl shadow-yellow-500/50 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-eye-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                      Lihat Menu Lengkap
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex space-x-3">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                index === currentSlide
                  ? 'bg-red-500 scale-125'
                  : 'bg-white/50 hover:bg-white/80'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Scroll Down Indicator */}
      <div className="absolute bottom-8 right-8 z-20 animate-bounce">
        <button
          onClick={() => scrollToSection('#menu')}
          className="w-12 h-12 bg-red-600/80 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer"
        >
          <i className="ri-arrow-down-line w-6 h-6 flex items-center justify-center"></i>
        </button>
      </div>
    </section>
  );
}
